/**
 * AgeReattestationGate — non-dismissable modal that fires when a talent's
 * DOB attestation is > 365 days old (or the env-configured interval).
 *
 * Design decisions (locked in iter 134):
 *   - Shows COMPUTED current age only. Never decrypts + surfaces the raw DOB.
 *   - Two actions: "Yes, still accurate" (stamps ``age_reattested_at``)
 *     OR "No, needs correction" (opens a mailto to support + writes a
 *     Compliance flag). Talent can NEVER self-mutate their DOB — that
 *     stays support-only per iter 131 policy.
 *   - Modal has no dismiss / X button. Talent must pick one of the two.
 */
import React, { useEffect, useState } from "react";
import { CalendarCheck2, ShieldCheck, Loader2, CheckCircle2, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const AgeReattestationGate = () => {
    const [status, setStatus] = useState(null); // { due, current_age, interval_days, days_since_last }
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState("confirm"); // confirm | dispute
    const [note, setNote] = useState("");

    useEffect(() => {
        let cancelled = false;
        api.get("/professional/age-reattest/status")
            .then(({ data }) => { if (!cancelled) setStatus(data); })
            .catch(() => { /* fail soft — no gate is safer than a broken loop */ });
        return () => { cancelled = true; };
    }, []);

    if (!status || !status.due) return null;

    const confirm = async () => {
        setBusy(true);
        try {
            await api.post("/professional/age-reattest", { still_accurate: true });
            toast.success("Thanks — your age is confirmed for another year.");
            setStatus({ ...status, due: false });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save. Try again.");
        } finally { setBusy(false); }
    };

    const dispute = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/professional/age-reattest", {
                still_accurate: false,
                dispute_note: note.trim() || null,
            });
            toast.success("Compliance has been notified. Opening your email so you can send us the correction.");
            if (data?.support_mailto) {
                window.location.href = data.support_mailto;
            }
            setStatus({ ...status, due: false });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save. Try again.");
        } finally { setBusy(false); }
    };

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="age-reattest-gate"
            role="dialog"
            aria-modal="true"
            aria-labelledby="age-reattest-title"
        >
            <div className="w-full max-w-lg rounded-2xl border border-border bg-background shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-br from-emerald-500/10 to-sky-500/10 border-b border-border p-5">
                    <div className="inline-flex items-center gap-2 text-emerald-700 dark:text-emerald-400 text-xs font-semibold uppercase tracking-wide">
                        <ShieldCheck size={13} /> Annual age check
                    </div>
                    <h2 id="age-reattest-title" className="mt-2 font-heading font-semibold text-2xl text-foreground">
                        Quick check — is your age still accurate?
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Simonara asks every talent once a year so no old typo quietly hangs around on your profile.
                    </p>
                </div>

                {mode === "confirm" && (
                    <div className="p-5 space-y-4">
                        {status.current_age != null ? (
                            <>
                                <div className="rounded-xl border border-border bg-muted/30 p-4 flex items-center gap-3">
                                    <CalendarCheck2 size={20} className="text-emerald-600 shrink-0" />
                                    <div>
                                        <div className="text-xs uppercase text-muted-foreground">Age on file</div>
                                        <div className="text-2xl font-heading font-semibold text-foreground" data-testid="age-reattest-current-age">
                                            {status.current_age}
                                        </div>
                                    </div>
                                </div>
                                <p className="text-sm text-foreground">
                                    Are you still <strong>{status.current_age}</strong>?
                                </p>
                            </>
                        ) : (
                            <p className="text-sm text-foreground" data-testid="age-reattest-current-age">
                                We don't have an age on file for you. Please confirm your date of birth is accurate on record — if it isn't, choose "No, needs correction" below.
                            </p>
                        )}
                        <div className="flex flex-col sm:flex-row gap-2">
                            <button
                                onClick={confirm}
                                disabled={busy}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
                                data-testid="age-reattest-confirm-btn"
                            >
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                                Yes, still accurate
                            </button>
                            <button
                                onClick={() => setMode("dispute")}
                                disabled={busy}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-border text-foreground hover:bg-muted px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
                                data-testid="age-reattest-dispute-btn"
                            >
                                <AlertTriangle size={14} /> No, needs correction
                            </button>
                        </div>
                        <p className="text-[11px] text-muted-foreground text-center italic">
                            You can't change your date of birth yourself — for your safety, only Simonara support can update it after verifying ID.
                        </p>
                    </div>
                )}

                {mode === "dispute" && (
                    <div className="p-5 space-y-4">
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                            <div className="inline-flex items-center gap-2 text-amber-700 dark:text-amber-400 text-xs font-semibold uppercase">
                                <AlertTriangle size={13} /> Correction requested
                            </div>
                            <p className="mt-2 text-sm text-foreground">
                                Tell us what's wrong (optional). When you click Continue we'll notify Compliance and open your email so you can send us proof.
                            </p>
                        </div>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                            maxLength={500}
                            placeholder="e.g. 'I typed my birth year wrong — I'm actually 24, not 42.'"
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            data-testid="age-reattest-dispute-note"
                        />
                        <div className="text-[10px] text-muted-foreground text-right">{note.length}/500</div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <button
                                onClick={() => setMode("confirm")}
                                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border text-foreground hover:bg-muted px-4 py-2 text-sm"
                                data-testid="age-reattest-back-btn"
                            >
                                Back
                            </button>
                            <button
                                onClick={dispute}
                                disabled={busy}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
                                data-testid="age-reattest-dispute-submit"
                            >
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                                Continue &amp; open email
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgeReattestationGate;
